/**
 * Stand-in client chats for the Clients space (mockAdapter.js uses this).
 *
 * Client people carry kind: 'client' and a small profile for the panel
 * beside the chat. Messages from the salon are written by a team member;
 * internal notes carry note: true; the automated texts and emails the
 * client was sent (reminders, review requests, campaigns) carry auto: {...}
 * and are drawn as quiet lines, salon side only.
 */
const minsAgo = (n) => new Date(Date.now() - n * 60000).toISOString();
const day = 60 * 24;

export const CLIENTS = [
    {
        id: 'cl-anika', name: 'Anika Patel', kind: 'client', role: 'Client',
        profile: {
            since: '2023', visits: 14, tags: ['Colour', 'Regular'],
            alert: 'Patch test 12 Mar 2026. Sensitive scalp, no bleach on the roots.',
            next: { when: 'Fri 17 Oct, 14:00', what: 'Balayage, Cut and finish', with: 'Sophie' },
            formula: 'Wella Koleston 6/0 + 7/1, 20 vol. Balayage lightener 9%, toned 9/16.',
            notes: ['Prefers a quiet appointment', 'Oat milk latte'],
        },
    },
    {
        id: 'cl-grace', name: "Grace O'Neill", kind: 'client', role: 'New client',
        reach: { channel: 'text', to: '07700 900481', repliesCome: true, channels: [{ channel: 'sms', to: '07700 900481', pence: 8 }] },
        profile: { since: 'Today', visits: 0, tags: ['New client'], notes: ['Found us on Instagram'] },
    },
    {
        id: 'cl-tom', name: 'Tom Hughes', kind: 'client', role: 'Client',
        // Texts from Beu's number: Tom's replies can't come back (no business number in the demo for him)
        reach: { channel: 'text', to: '07700 900217', repliesCome: false, emailRepliesCome: true, channels: [{ channel: 'sms', to: '07700 900217', pence: 8 }, { channel: 'email', to: 'tom.hughes@example.com', pence: 2 }] },
        profile: { since: '2024', visits: 22, tags: ['Barbering'], next: { when: 'Today, 09:30', what: 'Skin fade', with: 'Marcus' }, notes: ['Number 1 on the sides'] },
    },
    {
        id: 'cl-ola', name: 'Ola Bakare', kind: 'client', role: 'Client',
        profile: { since: '2025', visits: 6, tags: ['Nails'], next: { when: 'Sat 18 Oct, 10:30', what: 'Gel manicure', with: 'Priya' } },
    },
    {
        id: 'cl-ruth', name: 'Ruth Evans', kind: 'client', role: 'Client',
        reach: { channel: 'text', to: '07700 900356', repliesCome: true, emailRepliesCome: true, channels: [{ channel: 'sms', to: '07700 900356', pence: 8 }, { channel: 'email', to: 'ruth.evans@example.com', pence: 2 }] },
        profile: { since: '2024', visits: 9, tags: ['Lashes'], next: { when: 'Tue 21 Oct, 16:00', what: 'Lash lift', with: 'Jade' } },
    },
];

/** A call Bailey answered for Ola (the card in her chat; the real one comes from the call's transcript). */
export const OLA_CALL = () => ({
    id: 'o0', conversationId: 'k-ola', authorId: 'bailey', createdAt: minsAgo(day + 210), text: '', attachments: [], reactions: {}, mentions: [], channel: 'call',
    call: {
        seconds: 186, answeredBy: 'bailey', outcome: 'Took a message for Priya', phone: '07700 900644',
        summary: 'Ola asked to change her nail colour for Saturday and wanted Priya to confirm.',
        transcript: [
            { who: 'bailey', text: 'Hi, thanks for calling Hair LAB, this is Bailey. How can I help?' },
            { who: 'client', text: "Hi, it's Ola. I've got nails with Priya on Saturday. Can I change the colour?" },
            { who: 'bailey', text: "Of course. Priya's with a client, so I'll pass it on and she'll message you to confirm. Anything else?" },
            { who: 'client', text: "No, that's it. Thank you!" },
        ],
    },
});

/** Two more for texting rules (14 Oct 2026): Megan replied STOP; Hannah only has email. */
export const TEXTING_CLIENTS = [
    {
        id: 'cl-megan', name: 'Megan Doyle', kind: 'client', role: 'Client',
        reach: { channel: 'text', to: '07700 900731', repliesCome: true, emailRepliesCome: true, textsOff: { at: minsAgo(day + 30), by: 'stop-reply' },
            channels: [{ channel: 'sms', to: '07700 900731', pence: 8 }, { channel: 'email', to: 'megan.doyle@example.com', pence: 2 }] },
        profile: { since: '2025', visits: 4, tags: ['Brows'], next: { when: 'Mon 20 Oct, 11:00', what: 'Brow lamination', with: 'Jade' } },
    },
    {
        id: 'cl-hannah', name: 'Hannah Lee', kind: 'client', role: 'Client',
        reach: { channel: 'email', to: 'hannah.lee@example.com', emailRepliesCome: false, channels: [{ channel: 'email', to: 'hannah.lee@example.com', pence: 2 }] },
        profile: { since: '2026', visits: 2, tags: ['Cut'], next: { when: 'Thu 16 Oct, 17:30', what: 'Cut and finish', with: 'Sophie' } },
    },
];

export function textingChats(meId) {
    const conv = (id, clientId, extra) => ({
        id, space: 'clients', kind: 'client', clientId, memberIds: [meId, 'tm-sophie', 'tm-jade', 'tm-marcus', 'tm-priya', 'tm-leah', clientId],
        pinned: false, muted: false, createdAt: minsAgo(day * 30), readUpTo: { [meId]: minsAgo(0) }, assigneeId: null, source: 'app', ...extra,
    });
    const m = (id, cid, authorId, ago, text, extra = {}) => ({ id, conversationId: cid, authorId, createdAt: minsAgo(ago), text, attachments: [], reactions: {}, mentions: [], ...extra });
    const conversations = {
        'k-megan': conv('k-megan', 'cl-megan', { assigneeId: 'tm-jade', source: 'reminder', channel: 'sms', lastMessageAt: minsAgo(day + 30) }),
        'k-hannah': conv('k-hannah', 'cl-hannah', { assigneeId: 'tm-sophie', source: 'app', channel: 'email', lastMessageAt: minsAgo(day * 2) }),
    };
    const messages = {
        'k-megan': [
            m('mg0', 'k-megan', 'system', day + 60, '', { auto: { label: 'Reminder', channel: 'Text', status: 'Delivered' } }),
            m('mg1', 'k-megan', 'cl-megan', day + 30, 'STOP', { channel: 'sms' }),
            m('mg2', 'k-megan', 'system', day + 30, '', { system: { text: 'Megan turned off texts from the salon' } }),
        ],
        'k-hannah': [
            m('hn1', 'k-hannah', 'cl-hannah', day * 2 + 20, 'Hi, could Sophie take a little more off than last time? Thanks, Hannah', { channel: 'email', subject: "Thursday's cut" }),
            m('hn2', 'k-hannah', 'tm-sophie', day * 2, 'Of course Hannah, see you Thursday!', { channel: 'email', status: 'sent' }),
        ],
    };
    return { conversations, messages };
}

export function seedClients(meId) {
    const conv = (id, clientId, extra) => ({
        id, space: 'clients', kind: 'client', clientId, memberIds: [meId, 'tm-sophie', 'tm-jade', 'tm-marcus', 'tm-priya', 'tm-leah', clientId],
        pinned: false, muted: false, createdAt: minsAgo(day * 60), readUpTo: {}, assigneeId: null, source: 'app', ...extra,
    });
    const conversations = {
        'k-anika': conv('k-anika', 'cl-anika', { assigneeId: 'tm-sophie', source: 'app',
            suggestion: { text: "Of course! I've added a toner, which adds 15 minutes, so you'll finish at 16:45. Love the colour, see you Friday.", note: 'Also adds a toner (+15 min, £25) to Friday' } }),
        'k-grace': conv('k-grace', 'cl-grace', { assigneeId: null, source: 'booking-page', channel: 'booking-page', bailey: { state: 'needs-you', since: minsAgo(31) } }),
        'k-tom': conv('k-tom', 'cl-tom', { assigneeId: 'tm-marcus', source: 'reminder', channel: 'sms' }),
        'k-ola': conv('k-ola', 'cl-ola', { assigneeId: 'tm-priya', source: 'app', channel: 'app', bailey: { state: 'paused', takenBy: 'tm-priya', since: minsAgo(day + 192) } }),
        'k-ruth': conv('k-ruth', 'cl-ruth', { assigneeId: 'tm-jade', source: 'bailey', channel: 'booking-page', bailey: { state: 'replying', since: minsAgo(day + 399) } }),
    };
    const m = (id, cid, authorId, ago, text, extra = {}) => ({ id, conversationId: cid, authorId, createdAt: minsAgo(ago), text, attachments: [], reactions: {}, mentions: [], ...extra });
    const auto = (id, cid, ago, label, channel, status, extra = {}) => m(id, cid, 'system', ago, '', { auto: { label, channel, status, ...extra } });
    const messages = {
        'k-anika': [
            auto('a0', 'k-anika', day * 27, 'Reminder', 'Text', 'Delivered'),
            m('a1', 'k-anika', 'cl-anika', day * 27 - 12, 'Thanks, see you then!', { replyToId: 'a0' }),
            m('a1e', 'k-anika', 'system', day * 26, '', { system: { text: 'Visit completed, Cut and finish with Sophie' } }),
            auto('a2', 'k-anika', day * 26 - 30, 'Review request', 'Email', 'Opened'),
            m('a3', 'k-anika', 'cl-anika', day * 26 - 48, 'Done! Love the cut', { replyToId: 'a2' }),
            auto('a4', 'k-anika', day + 600, 'Autumn colour offer', 'Email campaign', 'Opened', { marketing: true }),
            m('a5e', 'k-anika', 'system', day + 80, '', { system: { text: 'Anika moved her booking to Fri 17 Oct, 14:00' } }),
            m('a5', 'k-anika', 'cl-anika', day + 78, "Hi! I've moved to Friday, hope that's ok"),
            m('a6', 'k-anika', 'tm-sophie', day + 60, 'Perfect, see you Friday Anika', { status: 'sent' }),
            auto('a7', 'k-anika', 300, 'Reminder for Friday', 'Text', 'Not delivered', { failed: true }),
            m('a8', 'k-anika', 'cl-anika', 25, "Could I add a toner this time? Here's the colour I'm after"),
            m('a9', 'k-anika', 'cl-anika', 25, '', { attachments: [{ type: 'image', url: '', title: 'Colour reference', width: 640, height: 480, demoTone: ['#6b5240', '#c49a6c'] }] }),
            m('a10', 'k-anika', 'tm-marcus', 23, '@Sophie her patch test from March is still valid, and you are free after.', { note: true, mentions: ['tm-sophie'] }),
        ],
        'k-grace': [
            m('g1', 'k-grace', 'cl-grace', 32, "Hi! Do you do balayage on dark hair? Mine's almost black and I'd love something caramel", { channel: 'booking-page' }),
            m('g2', 'k-grace', 'bailey', 31, 'Thanks Grace! A stylist will reply shortly, usually within the hour.', { bailey: true, channel: 'booking-page' }),
            m('g3', 'k-grace', 'system', 31, '', { system: { text: 'Bailey handed this to the team: a colour question for a stylist' } }),
        ],
        'k-tom': [
            auto('t0', 'k-tom', 70, 'Reminder', 'Text', 'Delivered'),
            m('t1', 'k-tom', 'cl-tom', 49, 'Running 10 mins late, sorry!', { replyToId: 't0', channel: 'sms' }),
        ],
        'k-ola': [
            OLA_CALL(),
            m('oc', 'k-ola', 'system', day + 192, '', { system: { text: 'Priya took over from Bailey' } }),
            m('o1', 'k-ola', 'cl-ola', day + 200, 'Can I change my nail colour on Saturday?'),
            m('o2', 'k-ola', 'tm-priya', day + 190, 'Of course, bring a photo of what you like!', { status: 'sent' }),
            m('o3', 'k-ola', 'cl-ola', day + 185, 'Will do, see you Saturday!'),
        ],
        'k-ruth': [
            m('r1', 'k-ruth', 'cl-ruth', day + 400, 'Do you have anything Tuesday afternoon for a lash lift?', { channel: 'booking-page' }),
            m('r2', 'k-ruth', 'bailey', day + 399, "Yes, I've booked you in for Tue 21 Oct at 16:00 with Jade. See you then!", { bailey: true, channel: 'booking-page', card: { type: 'booking', id: 'demo-b-ruth', client: 'Ruth Evans', services: 'Lash lift', stylist: 'Jade', when: 'Tue 21 Oct, 16:00', dayShort: 'TUE', dayNum: 21 } }),
        ],
    };
    const texting = textingChats(meId);
    return { conversations: { ...conversations, ...texting.conversations }, messages: { ...messages, ...texting.messages } };
}

/** What a filled-in consultation form says, for the answers sheet. */
export const FORM_ANSWERS = {
    flagged: { q: 'Have you ever had a reaction to hair dye?', a: 'Yes', detail: 'Itchy scalp after a box dye in 2023', advice: 'Book a patch test at least 48 hours before' },
    answers: [
        { q: 'What are you hoping for?', a: 'Caramel balayage, low maintenance' },
        { q: 'Colour in the last 12 months?', a: 'Box dye, dark brown, about 8 months ago' },
        { q: 'Any medical conditions we should know about?', a: 'None' },
        { q: 'Photo consent for our socials', a: 'Yes, happy to be tagged' },
    ],
};

export const FORMS = [
    { id: 'f-consult', name: 'New client consultation', questions: 6, minutes: 2 },
    { id: 'f-patch', name: 'Patch test consent', questions: 4, minutes: 1 },
    { id: 'f-aftercare', name: 'Aftercare check-in', questions: 3, minutes: 1 },
];

export const SERVICES = [
    { id: 's-balayage', name: 'Balayage', minutes: 150, price: 120, deposit: 30 },
    { id: 's-cut', name: 'Cut and finish', minutes: 60, price: 55, deposit: 0 },
    { id: 's-colour', name: 'Colour consultation', minutes: 20, price: 0, deposit: 0 },
    { id: 's-fade', name: 'Skin fade', minutes: 40, price: 28, deposit: 0 },
    { id: 's-gel', name: 'Gel manicure', minutes: 45, price: 35, deposit: 10 },
];
